import { ArrowRight } from "lucide-react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router";
import { Component, lazy, Suspense, useEffect, type ErrorInfo, type ReactNode } from "react";
import { SiteFooter } from "./components/SiteFooter";
import { SiteHeader } from "./components/SiteHeader";
import { DownloadPage } from "./pages/DownloadPage";
import { HomePage } from "./pages/HomePage";

const ReleasesPage = lazy(async () => ({ default: (await import("./pages/ReleasesPage")).ReleasesPage }));
const RoadmapPage = lazy(async () => ({ default: (await import("./pages/RoadmapPage")).RoadmapPage }));
const AdminPage = lazy(async () => ({ default: (await import("./pages/AdminPage")).AdminPage }));

interface DeferredPageBoundaryProps {
  readonly children: ReactNode;
}

interface DeferredPageBoundaryState {
  readonly error: Error | null;
}

export class DeferredPageBoundary extends Component<DeferredPageBoundaryProps, DeferredPageBoundaryState> {
  override state: DeferredPageBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): DeferredPageBoundaryState {
    return { error: error instanceof Error ? error : new Error("页面资源载入失败") };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Deferred public route failed to load", error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="not-found shell" role="alert">
        <span className="eyebrow">页面资源没有载入</span>
        <h1>连接恢复后，重新载入这一页。</h1>
        <p>{this.state.error.message}。已打开的首页和下载信息不受影响。</p>
        <div className="hero-actions">
          <button className="button button--dark" type="button" onClick={() => window.location.reload()}>重新载入</button>
          <Link className="text-link" to="/">返回产品首页</Link>
        </div>
      </main>
    );
  }
}

function deferredPage(element: ReactNode, label: string, routeIdentity: string) {
  return (
    <DeferredPageBoundary key={routeIdentity}>
      <Suspense fallback={<main className="page-main shell" role="status">{label}</main>}>
        {element}
      </Suspense>
    </DeferredPageBoundary>
  );
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [hash, pathname]);
  return null;
}

function NotFoundPage() {
  return (
    <main className="not-found shell">
      <span className="eyebrow">404 · 没走丢，只是这一步不存在</span>
      <h1>回到清晰的学习路线。</h1>
      <p>这个地址没有对应页面。产品与下载在上方，路线图和版本说明可从页脚进入。</p>
      <Link className="button button--dark" to="/">返回首页 <ArrowRight size={17} /></Link>
    </main>
  );
}

export function App() {
  return (
    <>
      <ScrollManager />
      <SiteHeader />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/downloads" element={<DownloadPage />} />
        <Route path="/roadmap" element={deferredPage(<RoadmapPage />, "正在打开产品路线图…", "roadmap")} />
        <Route path="/releases" element={deferredPage(<ReleasesPage />, "正在读取版本说明…", "releases")} />
        <Route path="/admin" element={deferredPage(<AdminPage />, "正在打开管理后台…", "admin")} />
        <Route path="/updates" element={<Navigate to="/releases" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <SiteFooter />
    </>
  );
}
